'use client';

import Link from 'next/link';
import Image from 'next/image';
import React, { useState } from 'react';
import { sortPlans } from '@/components/esim/utils';
import { FaCreditCard } from 'react-icons/fa';
import { TbCreditCard, TbDeviceMobileDollar, TbCurrencyBitcoin, TbBrandPaypal, TbBrandStripe, TbCreditCardFilled, TbBrandAlipay } from 'react-icons/tb';
import AffiliateLink from '@/components/common/AffiliateLink';
import GfSubnav from '@/components/common/GfSubnav';
import GfCta from '@/components/common/GfCta';
import GfSteps from '@/components/common/GfSteps';
import GfProviderCard from '@/components/common/GfProviderCard';
import GfSectionMark from '@/components/common/GfSectionMark';
import StickyMobileCta from '@/components/common/StickyMobileCta';
import TourCard from '@/components/tours/TourCard';
import { declineCityName } from '@/lib/content/declension';
import { t } from '@/i18n/translations';


/**
 * Универсальный компонент страницы провайдера в журнальном стиле
 * Поддерживает все 6 сервисов: eSIM, VPN, Tours, Transport, Stay, Insurance.
 * Референс — «TripLinkHub new design/Провайдер.html»: hero с рейтингом и
 * спек-картой, витрина реальных туров, плюс-список для пунктов без описаний.
 */
export default function ModernProviderPage({
    content,
    lang = 'ru',
    country,
    countryName = null,
    vertical = 'esim',
    backHref,
    allToursUrl = null,
    allToursText = null,
    // background (heroBg страны) принимается роутами, но hero провайдера —
    // «бумажный» по референсу, без фото; проп оставлен в API на будущее
}) {
    const [selectedRegion, setSelectedRegion] = useState(country || 'local');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });
    const [visiblePromoCodes, setVisiblePromoCodes] = useState({});
    const [copiedCode, setCopiedCode] = useState(null);

    const isRussian = lang === 'ru';

    // Короткое имя бренда: content.name бывает длинным SEO-заголовком
    // («Viator – Экскурсии и туры в Грузии») — в крошках/CTA/секциях он шумит.
    const shortName = (content.shortName || content.name || '').trim();
    let nameSuffix = null;
    // Без учёта регистра: «LocalRent – аренда авто…» при бренде «Localrent»
    if (content.name && shortName && content.name !== shortName && content.name.toLowerCase().startsWith(shortName.toLowerCase())) {
        nameSuffix = content.name.slice(shortName.length).replace(/^[\s–—|-]+/, '').trim() || null;
    }
    const heroTitle = nameSuffix ? shortName : (content.name || shortName);

    // CTA logic: для туров оставляем только одну кнопку (site или allToursUrl)
    const primaryCtaHref =
        vertical === 'tours'
            ? ((content.site && content.site !== '#') ? content.site : allToursUrl)
            : ((content.site && content.site !== '#') ? content.site : null);

    // Локализация — все UI-тексты страницы провайдера в src/i18n/sections/provider.js
    const tx = t(lang, 'providerPage');
    const labels = {
        back: tx.back[vertical] || tx.back.stay,
        benefits: tx.benefits,
        how: vertical === 'esim' ? tx.howEsim : tx.how,
        audience: tx.audience,
        why: tx.why,
        features: tx.features,
        book: tx.cta.default,
        plans: tx.plans,
        paymentMethods: tx.paymentMethods,
        allTours: allToursText || tx.allTours,
        promocodes: tx.promocodes,
        tours: tx.tours,
    };

    const getPaymentIcon = (title) => {
        const normalized = (title || '').toLowerCase();
        const iconProps = { className: 'w-4 h-4', style: { color: 'var(--pine)' } };
        if (normalized.includes('apple') || normalized.includes('google')) return <TbDeviceMobileDollar {...iconProps} />;
        if (normalized.includes('крипто') || normalized.includes('crypto') || normalized.includes('биткоин') || normalized.includes('bitcoin')) return <TbCurrencyBitcoin {...iconProps} />;
        if (normalized.includes('paypal')) return <TbBrandPaypal {...iconProps} />;
        if (normalized.includes('stripe')) return <TbBrandStripe {...iconProps} />;
        if (normalized.includes('alipay')) return <TbBrandAlipay {...iconProps} />;
        if (normalized.includes('россий') || normalized.includes('russian')) return <TbCreditCardFilled {...iconProps} />;
        if (normalized.includes('card') || normalized.includes('карты')) return <TbCreditCard {...iconProps} />;
        return <FaCreditCard {...iconProps} />;
    };

    // Определяем URL для кнопки "Назад"
    const defaultBackHref = country
        ? `/${lang}/${country}/${vertical === 'esim' ? 'esim' : vertical === 'tours' ? 'tours' : vertical}`
        : `/${lang}/${vertical === 'esim' ? 'esim' : vertical}`;
    const finalBackHref = backHref || content.backHref || defaultBackHref;

    // Обработка сортировки для планов
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '↕';
        return sortConfig.direction === 'ascending' ? '↑' : '↓';
    };

    // Получаем доступные регионы из планов
    const getAvailableRegions = () => {
        if (!content.plans || typeof content.plans !== 'object' || Array.isArray(content.plans)) {
            return [];
        }
        return Object.keys(content.plans).filter(key => content.plans[key] && content.plans[key].length > 0);
    };

    const availableRegions = getAvailableRegions();

    // Устанавливаем начальный регион, если текущий не доступен
    React.useEffect(() => {
        if (availableRegions.length > 0 && !availableRegions.includes(selectedRegion)) {
            setSelectedRegion(availableRegions[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableRegions.join(',')]);

    // Получаем планы для текущего региона
    const getCurrentPlans = () => {
        if (!content.plans) return [];
        if (typeof content.plans === 'object' && !Array.isArray(content.plans)) {
            return content.plans[selectedRegion] || content.plans[country] || content.plans[availableRegions[0]] || [];
        }
        return content.plans;
    };

    const currentPlans = getCurrentPlans();
    const sortedPlans = sortConfig.key ? sortPlans(currentPlans, sortConfig) : currentPlans;

    // --- CRO: идентификатор провайдера для трекинга кликов ---
    const providerId = content.code || content.name;

    // --- CRO: парсер числа из строки цены ("12.50 $/ГБ" -> 12.5) ---
    const parsePrice = (str) => {
        if (typeof str !== 'string') return NaN;
        const m = str.replace(/\s/g, '').match(/[\d.,]+/);
        return m ? parseFloat(m[0].replace(',', '.')) : NaN;
    };

    // --- CRO: индекс самого выгодного тарифа (минимум $/ГБ, иначе минимум цены) ---
    const planMetric = (p) => {
        const ppg = parsePrice(p.pricePerGb);
        return Number.isFinite(ppg) && ppg > 0 ? ppg : parsePrice(p.price);
    };
    let bestPlanIdx = -1;
    if (vertical === 'esim' && sortedPlans.length > 1) {
        bestPlanIdx = sortedPlans.reduce((best, p, i, arr) => {
            const cur = planMetric(p);
            const prev = planMetric(arr[best]);
            return Number.isFinite(cur) && (!Number.isFinite(prev) || cur < prev) ? i : best;
        }, 0);
    }

    // --- CRO: "от X" — цена самого дешёвого тарифа (для якоря в CTA) ---
    let fromPrice = null;
    if (vertical === 'esim' && currentPlans.length > 0) {
        const cheapest = currentPlans.reduce((min, p) => {
            const cur = parsePrice(p.price);
            const prev = parsePrice(min.price);
            return Number.isFinite(cur) && (!Number.isFinite(prev) || cur < prev) ? p : min;
        }, currentPlans[0]);
        fromPrice = cheapest?.price || null;
    }

    // --- Витрина туров провайдера (реальные документы tour из каталога) ---
    const tours = vertical === 'tours' && content.toursShowcase?.items?.length
        ? content.toursShowcase
        : null;
    const fmtNum = (n) => new Intl.NumberFormat(isRussian ? 'ru-RU' : 'en-US').format(n);
    const rating = tours?.avgRating || content.rating || null;
    const reviewsTotal = tours?.reviewsTotal || content.reviews || null;
    let tourPriceFrom = null;
    if (tours?.minPrice) {
        const sym = tours.currency === 'USD' ? '$' : '€';
        tourPriceFrom = isRussian ? `${Math.round(tours.minPrice)} ${sym}` : `${sym}${Math.round(tours.minPrice)}`;
    }

    // --- CRO: конкретный текст CTA по вертикали + якорь цены для eSIM ---
    const ctaForVertical = tx.cta[vertical];
    labels.book = (typeof ctaForVertical === 'function' ? ctaForVertical(fromPrice) : ctaForVertical) || labels.book;
    const bestValueLabel = tx.bestValue;

    // Функции для работы с промокодами
    const togglePromoCode = (index) => {
        setVisiblePromoCodes(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const fallbackCopyPromoCode = (code) => {
        if (typeof document === 'undefined') return false;
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '-1000px';
        textarea.style.left = '-1000px';
        document.body.appendChild(textarea);
        textarea.select();
        let copied = false;
        try {
            copied = document.execCommand('copy');
        } catch {
            copied = false;
        }
        document.body.removeChild(textarea);
        return copied;
    };

    const copyPromoCode = async (code, index) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(code);
            } else {
                fallbackCopyPromoCode(code);
            }
        } catch {
            fallbackCopyPromoCode(code);
        }
        setCopiedCode(index);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    // Hero-теглайн — первое предложение описания; полное описание уходит в
    // «О провайдере» и рендерится только если оно длиннее теглайна (иначе
    // секция дублировала бы hero слово в слово).
    const description = typeof content.description === 'string' ? content.description.trim() : '';
    // Конец предложения — знак и пробел/конец строки: «Trip.com» резалось до «Trip.» на 12 страницах
    const firstSentenceMatch = description.match(/^[\s\S]*?[.!?](?=\s|$)/);
    const tagline = (firstSentenceMatch ? firstSentenceMatch[0] : description).trim();
    const aboutText = description && description !== tagline ? description : '';

    const hasPlans = vertical === 'esim' && content.plans && sortedPlans.length > 0;
    const sectionAvailability = {
        about: aboutText.length > 0,
        tours: !!tours,
        benefits: Array.isArray(content.benefits) && content.benefits.length > 0,
        features: Array.isArray(content.features) && content.features.length > 0,
        plans: hasPlans,
        how: Array.isArray(content.howItWorks) && content.howItWorks.length > 0,
        promotions: Array.isArray(content.promotions) && content.promotions.length > 0,
        paymentMethods: Array.isArray(content.paymentMethods) && content.paymentMethods.length > 0,
        audience: Array.isArray(content.whoIsItFor) && content.whoIsItFor.length > 0,
        why: Array.isArray(content.whyChoose) && content.whyChoose.length > 0,
        related: Array.isArray(content.relatedProviders) && content.relatedProviders.length > 0,
        languages: Array.isArray(content.languages) && content.languages.length > 0,
    };

    const verticalLabel = tx.vlabel[vertical] || vertical;
    const countryLabel = countryName || (country ? country.charAt(0).toUpperCase() + country.slice(1) : '');
    // Предложный падеж для заголовков («туры в Грузии»); lvovich склоняет и страны
    const countryIn = isRussian && countryLabel ? declineCityName(countryLabel, 'in') : countryLabel;

    // Языки провайдера выносятся в отдельную секцию страницы (см. ниже).
    const languages = Array.isArray(content.languages) ? content.languages : [];
    const reviewsLabel = reviewsTotal
        ? `${fmtNum(reviewsTotal)} ${tx.reviewsWord(reviewsTotal)}`
        : null;

    const regionLabel = (k) => {
        if (k === country || k === 'local') return tx.region.local;
        if (k === 'regional') return tx.region.regional;
        if (k === 'europe') return tx.region.europe;
        if (k === 'global') return tx.region.global;
        return k.charAt(0).toUpperCase() + k.slice(1);
    };

    const labelAbout = tx.eyebrow.about;
    const labelRelated = tx.eyebrow.related;

    const anchors = [
        sectionAvailability.about && { href: '#about', label: labelAbout },
        sectionAvailability.tours && { href: '#tours', label: labels.tours },
        sectionAvailability.plans && { href: '#plans', label: labels.plans },
        sectionAvailability.benefits && { href: '#benefits', label: labels.benefits },
        sectionAvailability.features && { href: '#features', label: labels.features },
        sectionAvailability.how && { href: '#how', label: labels.how },
        sectionAvailability.promotions && { href: '#promotions', label: labels.promocodes },
        sectionAvailability.paymentMethods && { href: '#paymentMethods', label: labels.paymentMethods },
        sectionAvailability.languages && { href: '#languages', label: tx.languages },
        sectionAvailability.audience && { href: '#audience', label: labels.audience },
        sectionAvailability.why && { href: '#why', label: labels.why },
        sectionAvailability.related && { href: '#related', label: labelRelated },
    ].filter(Boolean);

    const txt = (v) => (typeof v === 'string' ? v : (v?.title || ''));

    // Пункты без описаний (голые строки) в карточках выглядят пусто — такие
    // списки рендерим компактным плюс-списком (.pv-pc), как в референсе.
    const isPlainList = (items) => items.every((it) => typeof it === 'string' || !(it && typeof it === 'object' && it.description));

    const renderItemList = (items, detailsByIdx = null) => {
        const hasDetails = Array.isArray(detailsByIdx) && detailsByIdx.some(Boolean);
        if (isPlainList(items) && !hasDetails) {
            return (
                <div className="pv-pc solo">
                    <div className="col pros">
                        <ul>
                            {items.map((it, i) => <li key={i}>{txt(it)}</li>)}
                        </ul>
                    </div>
                </div>
            );
        }
        // 3 в ряд, если набор не делится на 4 (6 карточек → 2×3, а не 4+2)
        return (
            <div className={`gf-scen${items.length % 4 === 0 ? '' : ' cols-3'}`}>
                {items.map((it, i) => (
                    <article className="sc" key={i}>
                        <span className="n">{`// ${String(i + 1).padStart(2, '0')}`}</span>
                        <h3>{txt(it)}</h3>
                        {typeof it === 'object' && it.description
                            ? <p>{it.description}</p>
                            : (detailsByIdx?.[i] ? <p>{detailsByIdx[i]}</p> : null)}
                    </article>
                ))}
            </div>
        );
    };

    // Дефолтная svg-заглушка вместо логотипа смотрится сломанно — лучше буква
    const localLogoSrc = content.code ? `/images/providers/${content.code}.svg` : null;
    const logoSrc = content.logo && !/\/default\.svg$/.test(content.logo)
        ? content.logo
        : localLogoSrc;

    const stickyPrice = (tourPriceFrom || fromPrice)
        ? tx.stickyFrom(tourPriceFrom || fromPrice)
        : (countryLabel ? `${verticalLabel} · ${countryLabel}` : verticalLabel);

    return (
        <div className="gf-root">
            {/* ── HERO ─────────────────────────────────────────── */}
            <section className="pv-hero">
                <div className="pv-hero-in">
                    <div className="pv-lhs">
                        <nav className="pv-crumb">
                            {countryLabel && country && (<><Link href={`/${lang}/${country}`}>{countryLabel}</Link><span className="sep">/</span></>)}
                            <Link href={finalBackHref}>{verticalLabel}</Link><span className="sep">/</span><b>{shortName}</b>
                        </nav>
                        {rating && (
                            <div className="pv-id">
                                <div className="pv-rate">
                                    <span className="stars" aria-hidden>★★★★★</span>
                                    <span>{rating}{reviewsLabel ? ` · ${reviewsLabel}` : ''}</span>
                                </div>
                            </div>
                        )}
                        <h1>{heroTitle}</h1>
                        {nameSuffix && <p className="pv-sub">{nameSuffix}</p>}
                        {tagline && <p className="pv-tagline">{tagline}</p>}
                        <div className="pv-cta">
                            {primaryCtaHref && (
                                <AffiliateLink href={primaryCtaHref} provider={providerId} vertical={vertical} country={country} price={fromPrice} position="hero" className="pri">
                                    {labels.book} <b>→</b>
                                </AffiliateLink>
                            )}
                            {vertical !== 'insurance' && (
                                <Link className="sec" href={finalBackHref}>{labels.back}</Link>
                            )}
                        </div>
                    </div>
                    <div className="pv-brand">
                        {logoSrc
                            ? <Image src={logoSrc} alt={shortName} width={240} height={150} className="object-contain" />
                            : <span className="ph">{(shortName || '?').charAt(0)}</span>}
                    </div>
                </div>
            </section>

            {/* ── SUBNAV ───────────────────────────────────────── */}
            {anchors.length > 0 && (
                <GfSubnav lang={lang} leaf={shortName} anchors={anchors}
                    crumbs={[countryLabel && country && { href: `/${lang}/${country}`, label: countryLabel }]} />
            )}

            {/* ── ABOUT ────────────────────────────────────────── */}
            {sectionAvailability.about && (
                <section className="gf-sec" id="about" style={{ paddingBottom: 0 }}>
                    <GfSectionMark eyebrow={labelAbout} title={tx.aboutTitle(shortName)} />
                    <p className="gf-lead">{aboutText}</p>
                </section>
            )}

            {/* ── TOURS SHOWCASE (живой каталог) ───────────────── */}
            {sectionAvailability.tours && (
                <section className="gf-sec" id="tours">
                    <GfSectionMark
                        eyebrow={labels.tours}
                        tag={`${tours.total} ${tx.toursWord(tours.total)}`}
                        title={tx.toursShowcaseTitle(shortName, isRussian ? countryIn : countryLabel)}
                    />
                    <p className="gf-lead">{tx.toursShowcaseLead}</p>
                    <div className="ct-tours">
                        {tours.items.map((t) => (
                            <TourCard key={t._id} tour={t} lang={lang} countrySlug={country} position="provider-tours" />
                        ))}
                    </div>
                    {(allToursUrl || primaryCtaHref) && tours.total > tours.items.length && (
                        <div className="gf-more-wrap">
                            <AffiliateLink href={allToursUrl || primaryCtaHref} provider={providerId} vertical={vertical} country={country} position="tours_showcase" className="gf-more">
                                {tx.allToursLink(tours.total, shortName)}
                            </AffiliateLink>
                        </div>
                    )}
                </section>
            )}

            {/* ── PLANS (eSIM ledger) ──────────────────────────── */}
            {hasPlans && (
                <section className="gf-sec" id="plans">
                    <GfSectionMark eyebrow={labels.plans} tag={shortName} title={tx.plansTitle(shortName)} />
                    <p className="gf-lead">{tx.plansLead}</p>

                    {content.regions && availableRegions.length > 1 && (
                        <div className="gf-ops" role="group">
                            {availableRegions.map((rk) => (
                                <button type="button" key={rk} className={selectedRegion === rk ? 'on' : ''} onClick={() => setSelectedRegion(rk)}>{regionLabel(rk)}</button>
                            ))}
                        </div>
                    )}

                    <div className="gf-plans">
                        <div className="gf-plan-row head">
                            <span data-sort="data" className={sortConfig.key === 'data' ? 'act' : ''} onClick={() => requestSort('data')} role="button" tabIndex={0}>{tx.col.data}<i className="ar">{getSortIcon('data')}</i></span>
                            <span data-sort="validity" className={sortConfig.key === 'validity' ? 'act' : ''} onClick={() => requestSort('validity')} role="button" tabIndex={0}>{tx.col.days}<i className="ar">{getSortIcon('validity')}</i></span>
                            <span data-sort="price" className={sortConfig.key === 'price' ? 'act' : ''} onClick={() => requestSort('price')} role="button" tabIndex={0}>{tx.col.price}<i className="ar">{getSortIcon('price')}</i></span>
                            <span>{sortedPlans[0]?.pricePerDay ? tx.col.perDay : tx.col.perGb}</span>
                            {/* invisible clone of the row action so the header's last
                                (auto) track reserves the same width — keeps columns aligned */}
                            <span className="sel" aria-hidden="true" style={{ visibility: 'hidden' }}>{tx.select}</span>
                        </div>
                        {sortedPlans.map((plan, index) => {
                            const isBest = index === bestPlanIdx;
                            return (
                                <div className={`gf-plan-row ${isBest ? 'rec' : ''}`} key={index}>
                                    <span className="d"><span className="gbline">{plan.data}{isBest && <span className="recl">{bestValueLabel}</span>}</span></span>
                                    <span className="v">{plan.validity}</span>
                                    <span className="p">{plan.price}</span>
                                    <span className="per">{plan.pricePerDay || plan.pricePerGb || '—'}</span>
                                    <AffiliateLink href={plan.link || content.site} provider={providerId} vertical={vertical} country={country} price={plan.price} position="plan_row" className="sel">
                                        {tx.select}
                                    </AffiliateLink>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── BENEFITS ─────────────────────────────────────── */}
            {sectionAvailability.benefits && (
                <section className="gf-sec" id="benefits" style={{ paddingTop: (hasPlans || sectionAvailability.tours) ? 0 : undefined }}>
                    <GfSectionMark eyebrow={tx.eyebrow.pros} title={labels.benefits} />
                    <div className="pv-benefits">
                        {content.benefits.map((b, i) => {
                            const title = typeof b === 'string' ? b : (b?.title || '');
                            const sub = (b && typeof b === 'object')
                                ? (b.subtitle || b.desc || (typeof b.description === 'string' ? b.description : ''))
                                : (content.benefitsDetails?.[i] || '');
                            if (!title) return null;
                            return (
                                <div className="pv-benefit" key={i}>
                                    <h3>{title}</h3>
                                    {/* без title заголовком служит description — второй раз не выводим */}
                                    {sub && sub.trim() !== title.trim() && <p>{sub}</p>}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── FEATURES ─────────────────────────────────────── */}
            {sectionAvailability.features && (
                <section className="gf-sec" id="features" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={tx.eyebrow.capabilities} title={labels.features} />
                    {renderItemList(content.features)}
                </section>
            )}

            {/* ── HOW IT WORKS ─────────────────────────────────── */}
            {sectionAvailability.how && (
                <section className="gf-sec" id="how" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={tx.eyebrow.guide} tag={`${content.howItWorks.length} ${tx.stepsWord(content.howItWorks.length)}`} title={labels.how} />
                    <p className="gf-lead">{isRussian
                        ? 'Коротко по шагам: от выбора сервиса до подтверждения.'
                        : 'Step by step: from choosing the service to confirmation.'}</p>
                    <GfSteps steps={content.howItWorks} />
                </section>
            )}

            {/* ── PROMO ────────────────────────────────────────── */}
            {sectionAvailability.promotions && (
                <section className="gf-sec" id="promotions" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={tx.eyebrow.discounts} title={labels.promocodes} allHref={vertical === 'esim' ? `/${lang}/${country}/esim/promotions` : null} allLabel={tx.allPromoCodes} />
                    <div className="gf-promos">
                        {content.promotions.map((promotion, index) => {
                            const shown = visiblePromoCodes[index];
                            return (
                                <article className="pm" key={index}>
                                    <span className="who">{promotion.providerLogo ? <img src={promotion.providerLogo} alt={promotion.provider} /> : promotion.provider}</span>
                                    <h3>{promotion.title}</h3>
                                    {promotion.description && <p>{promotion.description}</p>}
                                    <div className="code">
                                        <span className={`v ${shown ? '' : 'hidden'}`}>{shown ? promotion.code : '• • • • • •'}</span>
                                        <button type="button" onClick={() => (shown ? copyPromoCode(promotion.code, index) : togglePromoCode(index))}>
                                            {shown ? (copiedCode === index ? tx.promo.copied : tx.promo.copy) : tx.promo.show}
                                        </button>
                                    </div>
                                    {promotion.validUntil && <span className="exp">{tx.promo.validUntil}{promotion.validUntil}</span>}
                                    {promotion.link && promotion.link !== '#' && (
                                        <AffiliateLink href={promotion.link} provider={providerId} vertical={vertical} country={country} position="promo" className="gf-alllink" style={{ marginTop: 14 }}>
                                            {tx.promo.use}
                                        </AffiliateLink>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── PAYMENT METHODS ──────────────────────────────── */}
            {sectionAvailability.paymentMethods && (
                <section className="gf-sec" id="paymentMethods" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={tx.eyebrow.payment} title={labels.paymentMethods} />
                    <div className="pv-pay-grid">
                        {content.paymentMethods.map((m, i) => {
                            const title = typeof m.title === 'string' ? m.title.trim() : String(m.title || '');
                            const details = typeof m.details === 'string' ? m.details.trim() : '';
                            return (
                                <article className="pv-pay-card" key={i} title={details || title}>
                                    <span className="pv-pay-icon" aria-hidden="true">{getPaymentIcon(title)}</span>
                                    <div className="pv-pay-copy">
                                        <h3>{title}</h3>
                                        {details && <p>{details}</p>}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── LANGUAGES ────────────────────────────────────── */}
            {sectionAvailability.languages && (
                <section className="gf-sec" id="languages" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={tx.eyebrow.support} title={tx.languages} />
                    <div className="pv-chips">
                        {languages.map((l, i) => (
                            <span className="pv-chip" key={i}>{l}</span>
                        ))}
                    </div>
                </section>
            )}

            {/* ── AUDIENCE ─────────────────────────────────────── */}
            {sectionAvailability.audience && (
                <section className="gf-sec" id="audience" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={tx.eyebrow.audience} title={labels.audience} />
                    {renderItemList(content.whoIsItFor)}
                </section>
            )}

            {/* ── WHY CHOOSE ───────────────────────────────────── */}
            {sectionAvailability.why && (
                <section className="gf-sec" id="why" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={tx.eyebrow.choice} title={labels.why} />
                    {renderItemList(content.whyChoose)}
                </section>
            )}

            {/* ── RELATED PROVIDERS ────────────────────────────── */}
            {sectionAvailability.related && (
                <section className="gf-sec" id="related" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={labelRelated} tag={content.relatedProviders.length} title={tx.relatedTitle} allHref={finalBackHref} allLabel={labels.back} />
                    <div className="gf-grid gf-grid-center">
                        {content.relatedProviders.map((p) => (
                            <GfProviderCard key={p.code || p.name} name={p.name} logo={p.logo} description={p.description}>
                                <Link className="visit" href={p.href}>{tx.relatedDetails}</Link>
                            </GfProviderCard>
                        ))}
                    </div>
                </section>
            )}

            {/* ── CTA ──────────────────────────────────────────── */}
            <GfCta eyebrow={`${verticalLabel}${countryLabel ? ` · ${countryLabel}` : ''}`} title={shortName}>
                <div className="gf-cta-btns">
                    {primaryCtaHref && (
                        <AffiliateLink href={primaryCtaHref} provider={providerId} vertical={vertical} country={country} price={fromPrice} position="bottom" className="gf-btn">
                            {labels.book} →
                        </AffiliateLink>
                    )}
                    {vertical !== 'insurance' && (
                        <Link className="gf-btn ghost" href={finalBackHref}>{labels.back} →</Link>
                    )}
                </div>
            </GfCta>

            {/* CRO sticky mobile CTA */}
            <StickyMobileCta
                href={primaryCtaHref}
                provider={providerId}
                vertical={vertical}
                country={country}
                price={fromPrice}
                name={shortName}
                priceLabel={stickyPrice}
                label={labels.book}
            />
        </div>
    );
}
